import { createContext, use } from "react";
import type {
  QueryObserverResult,
  RefetchOptions,
} from "@tanstack/react-query";
import { noop } from "es-toolkit";

import type { UserRead } from "api/model";
import type { getMeResponse } from "api/users/users";
import { Permissions, type Permission } from "defs";

/** `/users/me` returns 403 only when Azure auth succeeded but the user is unverified. */
export const isPendingApprovalResponse = (response?: getMeResponse): boolean =>
  Number(response?.status) === 403;

export const toUserFromMeResponse = (
  response?: getMeResponse,
): UserRead | undefined =>
  Number(response?.status) === 200 ? response?.data : undefined;

export interface UserContext {
  isPending: boolean;
  isPendingApproval: boolean;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<getMeResponse, unknown>> | void;
  user?: UserRead;
  _default?: boolean;
}

const defaultContext: UserContext = {
  isPending: false,
  isPendingApproval: false,
  refetch: noop,
  _default: true,
};

export const UserContext = createContext(defaultContext);

export const useUser = () => {
  const ctx = use(UserContext);

  if (ctx._default) {
    console.warn("useUser() must be used within an <UserProvider />");
  }

  return ctx;
};

const permissionMap: Partial<Record<keyof UserRead, Permission>> = {
  is_verified: Permissions.USER,
  is_superuser: Permissions.ADMIN,
};

export const usePermissions = (): Permission[] => {
  const ctx = use(UserContext);

  if (ctx._default) {
    console.warn("usePermissions() must be used within an <UserProvider />");
  }

  return Object.keys(permissionMap).reduce<Permission[]>(
    (acc, key) =>
      ctx.user?.[key as keyof UserRead]
        ? [...acc, permissionMap[key as keyof UserRead]!]
        : acc,
    [],
  );
};
